<template>
  <div class="weather-panel" :class="[{ 'weather-panel--ground-station': variant === 'ground-station' }, { 'l-h-50 m-l-10': variant !== 'ground-station' }]">
    <UiButton tone="neutral" class="weather-trigger" size="small" :loading="loading" round @click="openWeather">
      <span class="weather-icon">{{ $t(weatherIcon(current.icon, current.text)) }}</span>
      <span v-if="current.temp !== undefined">{{ current.temp }}°</span>
      <span class="weather-text">{{ $t(current.text || (useShopProp && !shop ? '请选择商铺' : '天气')) }}</span>
      <span v-if="current.windScale" class="weather-wind">{{ $t("{0}级风", [(current.windScale)]) }}</span>
    </UiButton>

    <UiDialog v-model="visible" custom-class="weather-dialog"
      width="720px" top="8vh">
      <template slot="title">
        <div class="weather-title">
          <span>{{ $t("{0}天气", [(weatherData.shop ? weatherData.shop.name : '商铺')]) }}</span>
          <small v-if="weatherData.update_time">{{ $t("更新于 {0}", [$t(formatDateTime(weatherData.update_time))]) }}</small>
        </div>
      </template>

      <div v-loading="loading" class="weather-content">
        <UiAlert v-if="errorMessage" :title="$t(errorMessage)" type="warning" :closable="false" show-icon />

        <template v-if="weatherData.current">
          <section class="current-card">
            <div class="current-summary">
              <span class="current-icon">{{ $t(weatherIcon(current.icon, current.text)) }}</span>
              <div>
                <div><strong>{{ current.temp }}°</strong> <span>{{ $t(current.text) }}</span></div>
                <small>{{ $t("体感 {0}°", [(current.feelsLike)]) }}</small>
              </div>
            </div>
            <div class="weather-metrics">
              <div><span>{{ $t("风况") }}</span><strong>{{ $t("{0} {1}级", [$t(current.windDir), current.windScale]) }}</strong></div>
              <div><span>{{ $t("风速") }}</span><strong>{{ current.windSpeed }} km/h</strong></div>
              <div><span>{{ $t("湿度") }}</span><strong>{{ current.humidity }}%</strong></div>
              <div><span>{{ $t("能见度") }}</span><strong>{{ current.vis }} km</strong></div>
              <div><span>{{ $t("降水") }}</span><strong>{{ current.precip }} mm</strong></div>
              <div><span>{{ $t("气压") }}</span><strong>{{ current.pressure }} hPa</strong></div>
            </div>
          </section>

          <UiAlert v-for="alert in weatherData.alerts" :key="alert.id"
            class="weather-alert" :title="$t(alert.headline || alertName(alert))" type="error"
            :description="$t(alertDescription(alert))" :closable="false" show-icon />

          <section v-if="weatherData.minutely && weatherData.minutely.summary" class="weather-section rain-summary">
            <h4>{{ $t("未来两小时降水") }}</h4>
            <p>{{ weatherData.minutely.summary }}</p>
          </section>

          <section v-if="weatherData.hourly && weatherData.hourly.length" class="weather-section">
            <h4>{{ $t("逐小时预报") }}</h4>
            <div class="hourly-list">
              <div v-for="item in weatherData.hourly" :key="item.fxTime" class="hourly-item">
                <span>{{ $t(formatHour(item.fxTime)) }}</span>
                <b>{{ $t(weatherIcon(item.icon, item.text)) }}</b>
                <strong>{{ item.temp }}°</strong>
                <small>{{ $t("风 {0} km/h", [(item.windSpeed)]) }}</small>
                <small>{{ $t("降水 {0}%", [(item.pop || 0)]) }}</small>
              </div>
            </div>
          </section>

          <section v-if="weatherData.daily && weatherData.daily.length" class="weather-section">
            <h4>{{ $t("未来三天") }}</h4>
            <div class="daily-list">
              <div v-for="item in weatherData.daily" :key="item.fxDate" class="daily-item">
                <span>{{ $t(formatDay(item.fxDate)) }}</span>
                <strong>{{ $t(weatherIcon(item.iconDay, item.textDay)) }} {{ $t(item.textDay) }}</strong>
                <span>{{ item.tempMin }}° / {{ item.tempMax }}°</span>
                <small>{{ $t("{0} {1}级", [$t(item.windDirDay), item.windScaleDay]) }}</small>
              </div>
            </div>
          </section>

          <div class="weather-source">{{ $t("天气数据：") }}<a :href="weatherData.fx_link || 'https://www.qweather.com/'" target="_blank" rel="noopener">{{ $t("和风天气") }}</a>
          </div>
        </template>
      </div>
    </UiDialog>
  </div>
</template>

<script>
import axios from 'axios'

const weatherClient = axios.create({ timeout: 12000 })
delete weatherClient.defaults.headers.common.Token
delete weatherClient.defaults.headers.common.Authorization

export default {
  name: 'WeatherPanel',
  props: {
    variant: { type: String, default: 'default' },
    shop: { type: Object, default: null },
    useShopProp: { type: Boolean, default: false }
  },
  data () {
    return {
      visible: false,
      loading: false,
      weatherData: {},
      errorMessage: '',
      refreshTimer: null
    }
  },
  computed: {
    currentShop () {
      if (this.useShopProp) return this.shop
      return this.$store.state.shopList.find(item => String(item.shop_id) === String(this.$store.state.user.shop_id)) || null
    },
    current () {
      return this.weatherData.current || {}
    },
    shopLocationKey () {
      const shop = this.currentShop
      return shop ? [shop.shop_id, shop.latitude, shop.longitude].join('|') : ''
    }
  },
  watch: {
    $locale () {
      if (this.currentShop) this.fetchWeather(true)
    },
    current: {
      immediate: true,
      deep: true,
      handler (value) {
        this.$emit('change', value || {})
      }
    },
    shopLocationKey: {
      immediate: true,
      handler (value) {
        if (value) this.fetchWeather()
        else {
          this.weatherData = {}
          this.errorMessage = ''
        }
      }
    }
  },
  mounted () {
    this.refreshTimer = setInterval(() => this.fetchWeather(true), 15 * 60 * 1000)
  },
  beforeDestroy () {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  methods: {
    async fetchWeather (silent = false, force = false) {
      if (!this.currentShop || this.loading) return
      this.loading = true
      if (!silent) this.errorMessage = ''
      try {
        const cached = force ? null : this.readWeatherCache()
        if (cached) {
          this.weatherData = cached
          this.errorMessage = ''
          return
        }

        const longitudeValue = Number(this.currentShop.longitude)
        const latitudeValue = Number(this.currentShop.latitude)
        if (!Number.isFinite(longitudeValue) || !Number.isFinite(latitudeValue)) throw new Error('商铺经纬度不正确')
        const longitude = longitudeValue.toFixed(2)
        const latitude = latitudeValue.toFixed(2)
        const location = `${longitude},${latitude}`
        const lang = this.$locale === 'en' ? 'en' : 'zh'
        const commonParams = { location, lang, unit: 'm' }
        const responses = await Promise.all([
          this.weatherRequest('/v7/weather/now', commonParams),
          this.weatherRequest('/v7/weather/24h', commonParams),
          this.weatherRequest('/v7/weather/3d', commonParams),
          this.weatherRequest('/v7/minutely/5m', { location, lang }),
          this.weatherRequest(`/weatheralert/v1/current/${latitude}/${longitude}`, { localTime: 'true', lang })
        ])
        const now = responses[0]
        if (!now || String(now.code) !== '200' || !now.now) throw new Error('实时天气获取失败')

        const data = {
          shop: {
            shop_id: this.currentShop.shop_id,
            name: this.currentShop.name,
            latitude,
            longitude
          },
          current: now.now,
          hourly: responses[1] && String(responses[1].code) === '200' ? (responses[1].hourly || []).slice(0, 12) : [],
          daily: responses[2] && String(responses[2].code) === '200' ? (responses[2].daily || []).slice(0, 3) : [],
          minutely: responses[3] && String(responses[3].code) === '200' ? {
            summary: responses[3].summary || '',
            items: responses[3].minutely || []
          } : null,
          alerts: responses[4] && responses[4].metadata ? (responses[4].alerts || []).slice(0, 3) : [],
          update_time: now.updateTime || new Date().toISOString(),
          fx_link: now.fxLink || '',
          fetched_at: new Date().toISOString()
        }
        this.weatherData = data
        this.writeWeatherCache(data)
        this.errorMessage = ''
      } catch (error) {
        this.errorMessage = '天气服务暂时不可用'
      } finally {
        this.loading = false
      }
    },
    openWeather () {
      this.visible = true
      this.fetchWeather()
    },
    refreshWeather () {
      return this.fetchWeather(false, true)
    },
    async weatherRequest (path, params) {
      try {
        const settings = this.$store.state.settings
        const host = String(settings.qweatherHost || '').replace(/^https?:\/\//, '').replace(/\/$/, '')
        if (!host || !settings.qweatherApiKey) return null
        const res = await weatherClient.get(`https://${host}${path}`, {
          params: {
            ...params,
            key: settings.qweatherApiKey
          }
        })
        return res.data || null
      } catch (error) {
        return null
      }
    },
    weatherCacheKey () {
      return `flycube_weather_${this.shopLocationKey}_${this.$locale}`
    },
    readWeatherCache () {
      try {
        const raw = window.localStorage.getItem(this.weatherCacheKey())
        if (!raw) return null
        const cached = JSON.parse(raw)
        const cacheMinutes = Number(this.$store.state.settings.weatherCacheMinutes) || 15
        if (!cached.time || Date.now() - cached.time > cacheMinutes * 60000) return null
        return cached.data || null
      } catch (error) {
        return null
      }
    },
    writeWeatherCache (data) {
      try {
        window.localStorage.setItem(this.weatherCacheKey(), JSON.stringify({ time: Date.now(), data }))
      } catch (error) {}
    },
    weatherIcon (icon, text) {
      const code = parseInt(icon)
      if ((code >= 300 && code <= 303) || String(text).includes('雷')) return '⛈️'
      if ((code >= 300 && code < 400) || String(text).includes('雨')) return '🌧️'
      if ((code >= 400 && code < 500) || String(text).includes('雪')) return '❄️'
      if ((code >= 500 && code < 600) || /雾|霾|沙|尘/.test(String(text))) return '🌫️'
      if (code === 104 || String(text).includes('阴')) return '☁️'
      if ((code >= 101 && code <= 103) || String(text).includes('云')) return '⛅'
      if (code >= 150 && code <= 153) return '🌙'
      return '☀️'
    },
    formatHour (value) {
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? String(value || '').slice(11, 16) : `${String(date.getHours()).padStart(2, '0')}:00`
    },
    formatDay (value) {
      const date = new Date(`${value}T00:00:00`)
      if (Number.isNaN(date.getTime())) return value
      const today = new Date()
      const dayDiff = Math.round((date.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0)) / 86400000)
      if (dayDiff === 0) return '今天'
      if (dayDiff === 1) return '明天'
      if (dayDiff === 2) return '后天'
      return value.slice(5)
    },
    formatDateTime (value) {
      return String(value || '').replace('T', ' ').slice(0, 16)
    },
    alertName (alert) {
      return alert && alert.eventType ? `${alert.eventType.name || '天气'}预警` : '天气预警'
    },
    alertDescription (alert) {
      const text = String((alert && (alert.description || alert.instruction)) || '').replace(/\s+/g, ' ').trim()
      return text.length > 140 ? `${text.slice(0, 140)}...` : text
    }
  }
}
</script>

<style lang="scss" scoped>
.weather-panel {
  display: flex;
  align-items: center;
  height: 50px;
}

.weather-trigger {
  padding: var(--ui-space-sm);
  color: var(--el-text-color-primary);
  border-color: transparent;
  background: transparent;
  box-shadow: none;
}

@media (hover: hover) and (pointer: fine) {
  .weather-trigger:hover {
    color: var(--el-color-primary);
    border-color: transparent;
    background: rgba(var(--ui-color-primary-rgb), .08);
  }
}

.weather-panel--ground-station .weather-trigger {
  min-width: 0;
  padding: var(--ui-space-xs) 0;
  color: var(--ui-text-primary);
  border-color: transparent;
  border-radius: 0;
  background: transparent;
  font-size: var(--gs-font-body, 12px);
}

@media (hover: hover) and (pointer: fine) {
  .weather-panel--ground-station .weather-trigger:hover {
    color: var(--ui-color-primary);
    border-color: transparent;
    background: transparent;
  }
}

.weather-panel--ground-station .weather-icon {
  font-size: 16px;
}

.weather-panel--ground-station .weather-wind {
  color: var(--ui-text-secondary);
}

.weather-icon {
  margin-right: var(--ui-space-xs);
}

.weather-text,
.weather-wind {
  margin-left: var(--ui-space-xs);
}

.weather-content {
  min-height: 180px;
}

.weather-title {
  display: flex;
  align-items: baseline;
  gap: var(--ui-space-sm);
}

.weather-title small,
.weather-source,
.current-summary small,
.hourly-item small,
.daily-item small {
  color: var(--el-text-color-secondary);
}

.current-card {
  display: grid;
  grid-template-columns: 190px 1fr;
  gap: var(--ui-space-lg);
  padding: var(--ui-space-lg);
  border-radius: 12px;
  color: var(--el-color-white);
  background: linear-gradient(135deg, var(--el-color-primary), var(--ui-color-primary-hover));
}

.current-summary {
  display: flex;
  align-items: center;
  gap: var(--ui-space-md);
}

.current-summary strong {
  font-size: 40px;
}

.current-icon {
  font-size: 46px;
}

.weather-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--ui-space-sm);
}

.weather-metrics div {
  display: flex;
  flex-direction: column;
}

.weather-metrics span {
  opacity: .8;
  font-size: 12px;
}

.weather-alert,
.weather-section {
  margin-top: var(--ui-space-md);
}

.weather-section {
  padding: var(--ui-space-md);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 10px;
}

.weather-section h4,
.weather-section p {
  margin: 0;
}

.rain-summary p {
  margin-top: var(--ui-space-sm);
  color: var(--el-color-primary);
}

.hourly-list {
  display: flex;
  gap: var(--ui-space-sm);
  margin-top: var(--ui-space-sm);
  overflow-x: auto;
  padding-bottom: 4px;
}

.hourly-item {
  min-width: 82px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--ui-space-xs);
  padding: var(--ui-space-sm) var(--ui-space-xs);
  border-radius: 8px;
  background: var(--el-background-color-base);
}

.hourly-item b {
  font-size: 22px;
}

.daily-list {
  margin-top: var(--ui-space-sm);
}

.daily-item {
  display: grid;
  grid-template-columns: 70px 1fr 95px 130px;
  align-items: center;
  gap: var(--ui-space-sm);
  padding: var(--ui-space-sm) 0;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.daily-item:last-child {
  border-bottom: 0;
}

.weather-source {
  margin-top: var(--ui-space-sm);
  text-align: right;
  font-size: 12px;
}

@media (max-width: 900px) {
  .weather-wind {
    display: none;
  }
}

@media (max-width: 680px) {
  .weather-text {
    display: none;
  }

  .current-card {
    grid-template-columns: 1fr;
  }

  .weather-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .daily-item {
    grid-template-columns: 55px 1fr 80px;
  }

  .daily-item small {
    display: none;
  }
}
</style>

<style lang="scss">
@media (max-width: 760px) {
  .weather-dialog {
    width: calc(100% - (var(--ui-space-lg) * 2)) !important;
    margin-left: var(--ui-space-lg);
    margin-right: var(--ui-space-lg);
  }
}

.weather-dialog .el-dialog__body {
  max-height: calc(84vh - 70px);
  overflow-y: auto;
}
</style>
